import React, {useState} from "react";

import {Autocomplete} from "@mui/material";
import TextField from "@mui/material/TextField";
import {Button} from "@mui/material";

import {Aggregation} from "./classes/view/aggregations/Aggregation";
import {isEmpty} from "weed-js";


const getOperationOptions = (
    columnIndex,
    colDefs
) => {
    const column = colDefs.find(col => col.getIndex() === columnIndex);
    const availableOperations = [];
    for (const op of Object.values(Aggregation.operations)) {
        if (columnIndex === null) {
            availableOperations.push(op); // If no column is selected, show all operations
        } else if (op.isNumeric() && column?.isNumber) {
            availableOperations.push(op);
        } else if (op.isCategorical() && column?.isCategorical) {
            availableOperations.push(op);
        } else if (op.handlesDate() && column?.isDate) {
            availableOperations.push(op);
        }
    }
    return availableOperations.map(op => {
        return {value: op.displayName(), label: op.displayName()};
    });
}


const getColumnOptions = (
    operationName,
    colDefs
) => {
    const operation = Aggregation.operations[operationName];
    const availableColumns = [];
    for (const col of colDefs) {
        if (operationName === null) {
            availableColumns.push(col);  // If no operation is selected, show all columns
            continue;
        }
        if (operation?.isNumeric() && col.isNumber) {
            availableColumns.push(col);
            continue;
        }
        if (operation?.isCategorical() && col.isCategorical) {
            availableColumns.push(col);
            continue;
        }
        if (operation?.handlesDate() && col.isDate) {
            availableColumns.push(col);
        }
    }
    return availableColumns.map(col => {
        return {value: col.getIndex(), label: col.displayName};
    })
}


export const AggregationFormRow = ({view}) => {
    const colDefs = view.getActiveColumnDefs();  // Currently visible columns
    const pendingGroupBys = view.getPendingGroupBys();
    const appliedGroupBys = view.getAppliedGroupBys();
    const hasGroupBys = !isEmpty(pendingGroupBys) || !isEmpty(appliedGroupBys);

    const [generation, setGeneration] = useState(0);

    const [columnIndex, setColumnIndex] = useState(null);
    const [operationName, setOperationName] = useState(null);

    const disabled = operationName === null || columnIndex === null || !hasGroupBys;
    const disableDropdown = !hasGroupBys;

    return <div className="canold-table-view-menu-form-row" key={"aggregation-form-row-" + generation}>
        <Autocomplete  // Choose an Operation
            className={"canold-table-column-view-form-dropdown aggregation-form-dropdown"}
            filterSelectedOptions
            id={"canold-table-operation-dropdown"}
            name={"canold-table-operation-dropdown"}
            role={"canold-table-aggregate-operation-dropdown"}
            options={getOperationOptions(columnIndex, colDefs)}
            disablePortal={true}
            disabled={disableDropdown}
            isOptionEqualToValue={(option, value) => {
                return option.value === value.value;
            }}
            onChange={(evt, option, reason) => {
                if (option && option.value) {
                    setOperationName(option.value);
                } else {
                    setOperationName(null);
                }
            }}
            renderOption={(props, option) => {
                return <li {...props} key={"aggregation-operation-dropdown-" + option.value}>
                    {option.label}
                </li>
            }}
            onClick={(evt) => {
                evt.stopPropagation(); // Prevent event bubbling
            }}
            renderInput={(params) => {
                return <TextField
                    {...params}
                    label=""
                    onClick={(evt) => {
                        evt.stopPropagation(); // Prevent event bubbling
                    }}
                    placeholder="Sum/Max/Percentile/etc."
                    role={"canold-table-operation-dropdown-input"}
                    key={"canold-table-operation-dropdown-input"}
                />
            }}
        />
        <p>of</p>
        <Autocomplete // Choose a Column/Operand
            className={"canold-table-column-view-form-dropdown"}
            filterSelectedOptions
            id={"canold-table-aggregate-by-dropdown"}
            name={"canold-table-aggregate-by-dropdown"}
            role={"canold-table-aggregate-by-dropdown"}
            key={"canold-table-aggregate-by-dropdown "}
            options={getColumnOptions(operationName, colDefs)}
            disablePortal={true}
            disabled={disableDropdown}
            isOptionEqualToValue={(option, value) => {
                return option.value === value.value;
            }}
            onChange={(evt, option, reason) => {
                if (option && option.value !== null) {
                    setColumnIndex(option.value);
                } else {
                    setColumnIndex(null);
                }
            }}
            onClick={(evt) => {
                evt.stopPropagation(); // Prevent event bubbling
            }}
            renderOption={(props, option) => {
                return <li {...props} key={"canold-table-aggregate-by-dropdown-option-" + option.value}>
                    {option.label}
                </li>
            }}
            renderInput={(params) => {
                return <TextField
                    {...params}
                    label=""
                    onClick={(evt) => {
                        evt.stopPropagation(); // Prevent event bubbling
                    }}
                    placeholder="Column Name"
                    role={"canold-table-aggregate-by-dropdown-input"}
                    key={"canold-table-aggregate-by-dropdown-input"}
                />
            }}

        />
        <Button
            className={"canold-table-add-aggregate-button"}
            role={"canold-table-add-aggregate-button"}
            variant="contained"
            disabled={disabled}
            onClick={() => {
                const column = colDefs.find(col => col.getIndex() === columnIndex);
                const operationClass = Aggregation.operations[operationName];
                const operation = new operationClass({column: column});
                const stateChange = new Aggregation({
                    operation: operation,
                    column: column,
                    index: null, // Index will be set by the view
                });
                view.addStateChange(stateChange);
                view.onPendingStateChange([stateChange]);
                setGeneration(prev => prev + 1); // Force re-render
            }}
        >Add</Button>
    </div>
}
